import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  CircularProgress,
  Chip,
  Tooltip,
  Dialog,
  IconButton,
  AppBar,
  Toolbar,
  Button
} from '@mui/material';
import { FaTimes, FaChartPie, FaProjectDiagram } from 'react-icons/fa';
import { BiRadar } from 'react-icons/bi';
import EditableItemList from './EditableItemList';
import { ViewSwitchButton, ViewSwitchIcon } from '../../../../TrendRadarChart/components/Header/styles';
import SearchBar from '../../../../TrendRadarChart/components/SearchBar';
import useSearch from '../../../../TrendRadarChart/hooks/useSearch';
import { useRadarData } from '../../../../../hooks/useRadarData';
import useChartData from '../../../../TrendRadarChart/hooks/useChartData';
import { TREE_CHART_MODES } from '../../../../../constants/radar';
import { competencyTemplatesApi } from '../../../../../services/api';
import { useAlert } from '../../../../../contexts/AlertContext';
import { SKILL_TYPES } from '../../../../../constants/skillTypes';
import TrendRadarChart from '../../../../TrendRadarChart';
import { COLORS } from '../../../../../constants/radar';
import { SEGMENT_PROCESS_APPLIES_TO, COMPETENCY_ASSIGNMENT_STATUS } from '../../../../../constants/segmentProcess';

// Helper function to determine if a color is light
function isColorLight(hexColor) {
  if (!hexColor) return true;
  let color = hexColor.replace('#', '');
  if (color.length === 3) {
    color = color.split('').map(c => c + c).join('');
  }
  const r = parseInt(color.substr(0,2),16);
  const g = parseInt(color.substr(2,2),16);
  const b = parseInt(color.substr(4,2),16);
  // Perceived luminance formula
  const luminance = (0.299*r + 0.587*g + 0.114*b)/255;

  return luminance > 0.75;
}

// Custom Skill Level Selector Component
const SkillLevelSelector = ({ value, onChange, skillLevels, readonly }) => {
  // Find current level index (default to middle if not found)
  const currentLevelIndex = Math.max(0, skillLevels.findIndex(level => level.levelId === value));
  const currentLevel = skillLevels[currentLevelIndex];

  // Handle clicking on a specific section
  const handleLevelClick = (index) => {
    const selectedLevel = skillLevels[index];
    onChange(selectedLevel.levelId, selectedLevel.displayName);
  };

  const shouldDisableTooltip = (currentLevelId, levelId, readonly) => {
    return readonly && currentLevelId !== levelId;
  };
  
  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center',
        marginBottom: 0.5
      }}>
        <Typography 
          variant="caption"
          component="div" 
          sx={{ 
            color: 'text.secondary',
            display: 'block',
          }}
        >
          Level — <span style={{ 
            color: isColorLight(currentLevel?.color) ? '#333' : (currentLevel?.color || '#000') 
          }}>
            {currentLevel?.displayName || 'Not specified'}
          </span>
        </Typography>
      </Box>
      
      <Box 
        sx={{ 
          height: 40,
          backgroundColor: `${currentLevel?.color || '#000'}20`,
          borderRadius: 1,
          position: 'relative',
          display: 'flex',
          overflow: 'hidden',
          border: `${isColorLight(currentLevel?.color) ? '0.5px' : '0px'} solid ${currentLevel?.color || '#000'}`
        }}
      >
        {/* Background sections for visual separation */}
        {skillLevels.map((level, idx) => (
          <Box
            key={`bg-${level.levelId}`}
            sx={{
              width: `${100 / skillLevels.length}%`,
              height: '100%',
              position: 'relative',
              '&::after': idx < skillLevels.length - 1 ? {
                content: '"|"',
                position: 'absolute',
                right: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                color: currentLevel?.color || '#000',
                opacity: 1,
                fontSize: '17px',
                pointerEvents: 'none',
                fontWeight: 200
              } : {}
            }}
          />
        ))}

        {/* Active level indicator */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: `calc(${currentLevelIndex * (100 / skillLevels.length)}% - 2px)`,
            width: `calc(${100 / skillLevels.length}% + 2px)`,
            height: '100%',
            backgroundColor: currentLevel?.color || '#000',
            zIndex: 10,
            borderRadius: '4px'
          }}
        />

        {/* Clickable overlay sections with tooltips */}
        {skillLevels.map((level, index) => (
          <Tooltip 
            key={`click-${level.levelId}`}
            title={level.description || level.defaultDescription || ''}
            placement="top"
            arrow
            disableHoverListener={shouldDisableTooltip(currentLevel?.levelId, level.levelId, readonly)}
            disableFocusListener={shouldDisableTooltip(currentLevel?.levelId, level.levelId, readonly)}
            disableTouchListener={shouldDisableTooltip(currentLevel?.levelId, level.levelId, readonly)}
            disableInteractive={shouldDisableTooltip(currentLevel?.levelId, level.levelId, readonly)}
          >
            <Box
              onClick={() => !readonly && handleLevelClick(index)}
              sx={{
                position: 'absolute',
                left: `${index * (100 / skillLevels.length)}%`,
                width: `${100 / skillLevels.length}%`,
                height: '100%',
                cursor: 'pointer',
                zIndex: 20
              }}
            />
          </Tooltip>
        ))}
      </Box>
    </Box>
  );
};

// Helper function to check if CV competency assignment is disabled for a segment
const isCVCompetencyAssignmentDisabled = (segment) => {
  if (!segment?.segmentProcesses) return false;
  const cvProcess = segment.segmentProcesses.find(
    p => p.appliesTo === SEGMENT_PROCESS_APPLIES_TO.CV
  );
  return cvProcess?.competencyAssignment === COMPETENCY_ASSIGNMENT_STATUS.DISABLED;
};

const SkillsItems = ({
  skills = [],
  onSkillsChange,
  expandedIndex,
  setExpandedIndex,
  subcategoryId,
  firstLevelSegment,
  radarLoading,
  addButtonText = "Add a skill",
  emptyStateTitle = "You haven't added any skills yet.",
  emptyStateDescription = "Add skills to your profile.",
  hideLevels = false,
  globalSearch = false,
  singleSelection = false,
  readonly = false,
  showRadarNavigation = true,
  showRadarViewToggle = true,
}) => {
  // Compute effective hideLevels based on segment process configuration
  const effectiveHideLevels = hideLevels || isCVCompetencyAssignmentDisabled(firstLevelSegment);
  const { showAlert } = useAlert();
  
  // Track local search terms for each expanded skill
  const [localSearchTerm, setLocalSearchTerm] = useState('');
  
  // Track if search term has been manually edited
  const [searchEdited, setSearchEdited] = useState(false);
  
  // Keep reference to current skill name to avoid flashing UI
  const currentSkillRef = useRef('');
  
  // Use the radar data hook instead of manual fetching
  const { data: radarData, loading: localRadarLoading, error: radarError, refresh: refreshRadarData } = useRadarData();
  
  // Current subcategory derived from subcategoryId and radarData
  const [currentSubcategory, setCurrentSubcategory] = useState(null);
  
  // Add a state to track if we're loading a specific subcategory
  const [loadingSubcategory, setLoadingSubcategory] = useState(false);
  
  // Track previous subcategoryId to detect changes
  const prevSubcategoryIdRef = useRef(null);
  
  // State for radar dialog
  const [radarDialogOpen, setRadarDialogOpen] = useState(false);
  const [radarChartType, setRadarChartType] = useState(TREE_CHART_MODES.RADAR);
  
  // Use chart data hooks for radar functionality
  const {
    handleSelection,
  } = useChartData(radarData || {}, TREE_CHART_MODES.TREE);
  
  // Use the search hook with our radar data
  const {
    searchResults,
    showResults,
    isGlobalSearch,
    handleSearch,
    setIsGlobalSearch,
  } = useSearch(radarData || {}, globalSearch ? null : currentSubcategory);
  
  // State for competency levels
  const [competencyLevels, setCompetencyLevels] = useState([]);

  // Update currentSubcategory when subcategoryId or radarData changes
  useEffect(() => {
    // If subcategoryId changed, set loading state
    if (subcategoryId !== prevSubcategoryIdRef.current) {
      setLoadingSubcategory(true);
      prevSubcategoryIdRef.current = subcategoryId;
    }
    
    if (subcategoryId && radarData?.subcategories) {
      const subcategory = radarData.subcategories.find(
        subcat => subcat.id === subcategoryId
      );
      setCurrentSubcategory(subcategory || null);
      setLoadingSubcategory(false); // Found the subcategory (or confirmed it doesn't exist)
    } else if (subcategoryId) {
      // We have a subcategoryId but no radarData or subcategories yet
      setCurrentSubcategory(null);
      setLoadingSubcategory(true);
    } else {
      // No subcategoryId, so nothing to load
      setCurrentSubcategory(null);
      setLoadingSubcategory(false);
    }
  }, [subcategoryId, radarData]);
  
  // Set global search mode
  useEffect(() => {
    if (globalSearch) {
      setIsGlobalSearch(true);
    }
  }, [globalSearch, setIsGlobalSearch]);

  // Fetch competency templates only if we need them
  useEffect(() => {
    if (effectiveHideLevels) return;
    
    const fetchCompetencyTemplates = async () => {
      try {
        // Fetch technical competency templates
        const templates = await competencyTemplatesApi.getTemplatesByType(SKILL_TYPES.TECHNICAL);
        
        // If templates exist and have levels, transform the first template's levels
        if (templates && templates.length > 0 && templates[0].levels) {
          const transformedLevels = templates[0].levels
            .filter(level => level.level >= 0) // Filter out negative levels
            .map(level => ({
              level: level.level,
              displayName: level.name,
              color: level.color,
              defaultDescription: level.description,
              description: level.description,
              levelId: level.id
            }));
          
          setCompetencyLevels(transformedLevels);
        }
      } catch (error) {
        console.error('Failed to fetch competency templates:', error);
        showAlert('Failed to fetch competency templates', 'error', error);
      }
    };

    fetchCompetencyTemplates();
  }, [showAlert, effectiveHideLevels]);
  
  // Watch for expandedIndex changes to initialize search correctly
  useEffect(() => {
    if (expandedIndex !== null && skills && Array.isArray(skills) && skills[expandedIndex]) {
      const skill = skills[expandedIndex];
      
      // Keep track of the current skill name in the ref
      currentSkillRef.current = skill.name || '';
      
      // Reset search with current skill name
      if (skill.name) {
        // Initialize the local search term with the current skill name
        setLocalSearchTerm(skill.name);
        if (handleSearch && typeof handleSearch === 'function') {
          handleSearch({ target: { value: skill.name } });
        }
      } else {
        setLocalSearchTerm('');
      }
    }
  }, [expandedIndex, skills, handleSearch]);
  
  // Reset search term when item is collapsed
  useEffect(() => {
    if (expandedIndex === null) {
      // When all items are collapsed, reset the search state
      setLocalSearchTerm('');
      setSearchEdited(false);
    }
  }, [expandedIndex]);
  
  // Add an empty skill by default if singleSelection is true and no skills exist
  useEffect(() => {
    if (singleSelection && (!skills || skills.length === 0)) {
      const newSkill = createNewSkill();
      onSkillsChange([newSkill]);
      setExpandedIndex(0);
    }
  }, [singleSelection]); // Only run when singleSelection changes
  
  // Function to create a new empty skill
  const createNewSkill = () => {
    const defaultLevel = !effectiveHideLevels && competencyLevels.length > 0
      ? competencyLevels[Math.floor(competencyLevels.length / 2)]
      : null;

    return {
      id: null,
      name: '',
      level: !effectiveHideLevels && defaultLevel ? { id: defaultLevel.levelId, name: defaultLevel.displayName } : undefined,
      radarNode: null,
      belongsToRadar: true
    };
  };

  // Handle search result selection
  const handleSearchResultSelected = (skill, result) => {
    // Using destructuring with a try/catch to avoid errors if handleResultSelect is not available
    
    try {
      // Only call handleResultSelect if it exists and is a function
      if (handleSelection && typeof handleSelection === 'function') {
        handleSelection(result);
      }
    } catch (error) {
      console.error('Error calling handleResultSelect');
    }
    
    // Make sure result is valid before accessing properties
    if (!result) {
      console.error('Search result is undefined or null');
      return skill;
    }
    
    // Reset the search term to the selected value
    const newName = result.name || '';
    setLocalSearchTerm(newName);
    
    // Update the current skill ref with new name
    currentSkillRef.current = newName;
    
    // Reset edited state since we're selecting a result
    setSearchEdited(false);
    
    // Use setTimeout to ensure the search is updated after state changes
    setTimeout(() => {
      if (handleSearch && typeof handleSearch === 'function') {
        handleSearch({ target: { value: newName } });
      }
    }, 50);
    
    return {
      ...skill,
      id: result.leafSegmentId,
      name: newName,
      //radarNode: result,
      belongsToRadar: true
    };
  };
  
  // Handle local search change without updating the skill name
  const handleLocalSearchChange = (e) => {
    const value = e.target.value;
    setLocalSearchTerm(value);
    
    // Only mark as edited if value differs from current skill name
    if (value !== currentSkillRef.current) {
      setSearchEdited(true);
    } else {
      setSearchEdited(false);
    }
    
    handleSearch(e);
  };
  
  // Handle search clear - properly clear the search field
  const handleSearchClear = () => {
    setLocalSearchTerm('');
    setSearchEdited(false);
    handleSearch({ target: { value: '' } });
  };

  // Function to render the summary of a skill (collapsed view)
  const renderSkillSummary = (skill) => {
    const levelInfo = !effectiveHideLevels && competencyLevels.find(level => level.levelId === skill.level?.id);

    return (
      <>
        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
          {skill.name || '(Not specified)'}
          {skill.belongsToRadar === false && (
            <Chip
              label="custom"
              size="small"
              color="warning"
              variant="outlined"
              sx={{ 
                ml: 1, 
                height: 18,
                fontSize: '0.65rem',
                fontWeight: 'normal',
                '& .MuiChip-label': {
                  px: 0.8,
                  py: 0
                }
              }}
            />
          )}
        </Typography>
        {!effectiveHideLevels && skill.level && levelInfo && (
          <Typography variant="body2" style={{ color: isColorLight(levelInfo?.color) ? '#333' : (levelInfo?.color || '#000') }}>
            {levelInfo?.displayName || 'Not specified'}
          </Typography>
        )}
        {skill.radarNode && skill.radarNode.fullPath && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Radar Path: {skill.radarNode.fullPath}
          </Typography>
        )}
      </>
    );
  };

  // Function to render the edit form for a skill (expanded view)
  const renderSkillEditForm = (skill, onChange) => {
    // Update currentSkillRef with skill name (without useEffect)
    currentSkillRef.current = skill.name || '';
    
    // Determine if we should show the manual selection option
    const showManualOption = searchEdited && 
                            localSearchTerm && 
                            typeof localSearchTerm === 'string' &&
                            localSearchTerm.trim() !== '' && 
                            localSearchTerm.trim() !== (skill.name || '');
    
    return (
      <Box>
        <Box
          sx={{
            width: '100%',
            display: 'grid',
            gridTemplateColumns: effectiveHideLevels
              ? '1fr'
              : {
                  xs: '1fr',
                  sm: '7fr 5fr'
                },
            gap: '24px'
          }}
        >
          <Box>
            <Box sx={{ position: 'relative', zIndex: 1050 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                Skill Name
              </Typography>
              {/* SearchBar with enhanced loading state handling */}
              {localRadarLoading ? (
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1.5, 
                  height: '40px', 
                  pl: 1 
                }}>
                  <CircularProgress size={20} color="primary" />
                  <Typography variant="body2" color="text.secondary">
                    Loading technology radar data...
                  </Typography>
                </Box>
              ) : radarError ? (
                <Typography variant="body2" color="error">
                  Error loading radar data. Using regular input instead.
                  <TextField
                    fullWidth
                    variant="outlined"
                    value={skill.name || ''}
                    onChange={(e) => onChange({ ...skill, name: e.target.value })}
                    placeholder="Enter skill name"
                    size="small"
                  />
                </Typography>
              ) : (
                <Box sx={{ position: 'relative' }}>
                  <SearchBar
                    searchTerm={localSearchTerm}
                    searchResults={searchResults}
                    showResults={showResults}
                    isGlobalSearch={globalSearch || (!currentSubcategory ? true : false)}
                    selectedSubcategory={globalSearch ? null : currentSubcategory}
                    onSearch={handleLocalSearchChange}
                    onResultSelect={(result) => {
                      // When a search result is selected, update the skill with the selected node
                      onChange(handleSearchResultSelected(skill, result));
                    }}
                    onToggleSearchMode={setIsGlobalSearch}
                    showNoResultsMessage={false}
                    width="100%"
                    onClear={() => handleSearchClear()}
                    readonly={readonly}
                  />
                  
                  {/* Manual selection button only shown after user has edited the search term */}
                  {showManualOption && !singleSelection && (
                    <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" color="text.secondary">
                        Current name: <b>{skill.name || 'None'}</b>
                      </Typography>
                      <Typography 
                        variant="caption" 
                        color="primary"
                        sx={{ 
                          cursor: 'pointer', 
                          textDecoration: 'underline',
                          '&:hover': { opacity: 0.8 }
                        }}
                        onClick={() => {
                          onChange({ 
                            ...skill, 
                            name: localSearchTerm.trim(),
                            belongsToRadar: false
                          });
                          currentSkillRef.current = localSearchTerm.trim();
                          setSearchEdited(false);
                        }}
                      >
                        Use "{localSearchTerm.trim()}" as skill name
                      </Typography>
                    </Box>
                  )}
                  
                  {/* Message for skills that don't belong to the radar */}
                  {skill.name && skill.belongsToRadar === false && (
                    <Box 
                      sx={{ 
                        mt: 1, 
                        p: 1, 
                        backgroundColor: 'rgba(255, 190, 0, 0.08)',
                        borderRadius: 1,
                        borderLeft: '3px solid',
                        borderColor: 'warning.main'
                      }}
                    >
                      <Typography variant="caption" color="warning.dark">
                        Note: This skill is not part of the technology radar
                      </Typography>
                    </Box>
                  )}
                  
                  {/* Show the radar path if a node is selected */}
                  {skill.radarNode && skill.radarNode.fullPath && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      {skill.radarNode.fullPath}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          </Box>
          
          {!effectiveHideLevels && (
            <Box>
              <SkillLevelSelector
                value={skill.level?.id || (competencyLevels[Math.floor(competencyLevels.length / 2)]?.levelId || '')}
                onChange={(newLevelId, newLevelName) => {
                  onChange({
                    ...skill,
                    level: { id: newLevelId, name: newLevelName }
                  });
                }}
                skillLevels={competencyLevels}
                readonly={readonly}
              />
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  // Create the custom action button for radar browsing
  const radarBrowseButton = (
    <Button
      variant="outlined"
      color="primary"
      size="small"
      onClick={() => setRadarDialogOpen(true)}
      startIcon={<FaChartPie />}
      sx={{ 
        textTransform: 'none',
        fontWeight: 500,
        fontSize: '0.875rem',
      }}
    >
      Browse Technology Radar
    </Button>
  );

  return (
    <Box sx={{ position: 'relative', overflow: 'visible' }}>
      {/* Loading indicator for category description - show when we're loading radar data or a specific subcategory */}
      {(radarLoading || loadingSubcategory) && (
        <Box 
          sx={{ 
            mb: 3,
            p: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            backgroundColor: 'rgba(0, 0, 0, 0.02)',
            borderRadius: 1,
            borderLeft: '4px solid',
            borderColor: 'primary.main'
          }}
        >
          <CircularProgress size={20} color="primary" />
          <Typography variant="body2" color="text.secondary">
            Loading information...
          </Typography>
        </Box>
      )}
      
      {/* Display category description if available */}
      {!radarLoading && !loadingSubcategory && currentSubcategory && currentSubcategory.description && (
        <Box 
          sx={{ 
            mb: 3,
            p: 2,
            backgroundColor: 'rgba(0, 0, 0, 0.02)',
            borderRadius: 1,
            borderLeft: '4px solid',
            borderColor: 'primary.main'
          }}
        >
          <Typography 
            variant="body2" 
            sx={{ 
              fontStyle: 'italic',
              color: 'text.secondary',
              whiteSpace: 'pre-line'
            }}
          >
            {currentSubcategory.description}
          </Typography>
        </Box>
      )}
      
      <EditableItemList
        items={skills}
        setItems={onSkillsChange}
        expandedIndex={expandedIndex}
        setExpandedIndex={setExpandedIndex}
        createNewItem={createNewSkill}
        renderSummary={renderSkillSummary}
        renderEditForm={renderSkillEditForm}
        addButtonText={singleSelection ? null : addButtonText}
        emptyState={{
          title: emptyStateTitle,
          description: emptyStateDescription
        }}
        itemName="skill"
        singleSelection={singleSelection}
        customAction={radarBrowseButton}
        readonly={readonly}
      />

      {/* Full Screen Radar Dialog */}
      <Dialog
        fullWidth
        maxWidth="xl"
        open={radarDialogOpen}
        onClose={() => {
          setRadarDialogOpen(false);
          setRadarChartType(TREE_CHART_MODES.RADAR);
          refreshRadarData();
        }}
      >
        <AppBar position="static" color="transparent" elevation={0}>
          <Toolbar>
            <Typography variant="h6" sx={{ flexGrow: 1, color: COLORS.PRIMARY }}>
              Technology Radar
            </Typography>
            {showRadarViewToggle && (
              <ViewSwitchButton
                onClick={() => setRadarChartType(
                  radarChartType === TREE_CHART_MODES.TREE
                    ? TREE_CHART_MODES.RADAR
                    : TREE_CHART_MODES.TREE
                )}
                style={{ marginRight: 16 }}
              >
                <ViewSwitchIcon>
                  {radarChartType === TREE_CHART_MODES.TREE ? <BiRadar /> : <FaProjectDiagram />}
                </ViewSwitchIcon>
                {radarChartType === TREE_CHART_MODES.TREE ? 'Switch to Radar View' : 'Switch to Tree View'}
              </ViewSwitchButton>
            )}
            <IconButton
              edge="end"
              color="inherit"
              onClick={() => {
                setRadarDialogOpen(false);
                setRadarChartType(TREE_CHART_MODES.RADAR);
                refreshRadarData();
              }}
              aria-label="close"
            >
              <FaTimes />
            </IconButton>
          </Toolbar>
        </AppBar>
        <Box sx={{ height: 'calc(100vh - 64px)', overflow: 'hidden' }}>
          <TrendRadarChart
            chartType={radarChartType}
            configMode={TREE_CHART_MODES.TREE}
            hideHeader={true}
            hideNavigation={!showRadarNavigation}
            hideDescription={true}
            embedded={false}
          />
        </Box>
      </Dialog>
    </Box>
  );
};

export default SkillsItems; 